import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import * as LoanDB from '@/lib/personalLoansDb';
import { supabase } from '@/lib/supabase';
import RepaymentModal from './components/RepaymentModal';

function getStatusStyle(status: string) {
  const map: Record<string, string> = {
    'Aktif': 'bg-amber-100 text-amber-800',
    'Kısmi Ödendi': 'bg-sky-100 text-sky-800',
    'Ödendi': 'bg-emerald-100 text-emerald-800',
    'Gecikti': 'bg-red-100 text-red-800',
    'İptal': 'bg-foreground-100 text-foreground-500',
    'Hukuki Takip': 'bg-rose-100 text-rose-800',
  };
  return map[status] || 'bg-foreground-100 text-foreground-500';
}

function getMovementIcon(type: string) {
  const map: Record<string, string> = {
    'loan_given': 'ri-money-dollar-circle-line text-amber-500',
    'repayment': 'ri-arrow-go-back-line text-emerald-500',
    'adjustment_increase': 'ri-add-circle-line text-sky-500',
    'adjustment_decrease': 'ri-indeterminate-circle-line text-orange-500',
    'reversal': 'ri-arrow-go-back-line text-red-500',
    'cancellation': 'ri-close-circle-line text-red-500',
  };
  return map[type] || 'ri-file-list-3-line text-foreground-400';
}

function getMovementLabel(type: string) {
  const map: Record<string, string> = {
    'loan_given': 'Borç Verildi',
    'repayment': 'Geri Ödeme Alındı',
    'adjustment_increase': 'Düzeltme (Artış)',
    'adjustment_decrease': 'Düzeltme (Azalış)',
    'reversal': 'Ters Kayıt',
    'cancellation': 'İptal',
  };
  return map[type] || type;
}

function getBorrowerTypeLabel(type: string) {
  const map: Record<string, string> = {
    'customer': 'Müşteri',
    'employee': 'Personel',
    'supplier': 'Tedarikçi',
    'other': 'Diğer',
  };
  return map[type] || type;
}

export default function PersonalLoanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loan, setLoan] = useState<LoanDB.PersonalLoan | null>(null);
  const [movements, setMovements] = useState<LoanDB.PersonalLoanMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRepayment, setShowRepayment] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [loanRes, movRes] = await Promise.all([
        LoanDB.getPersonalLoanById(id),
        LoanDB.getPersonalLoanMovements(id),
      ]);
      if (loanRes.error) {
        setError(loanRes.error);
      } else if (!loanRes.data) {
        setError('Borç kaydı bulunamadı.');
      } else {
        setLoan(loanRes.data);
        setMovements(movRes.data || []);
      }
    } catch {
      setError('Veri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCancel = async () => {
    if (!loan || !cancelReason.trim()) return;
    setCancelling(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      const res = await LoanDB.cancelPersonalLoan({
        loan_id: loan.id,
        cancellation_reason: cancelReason.trim(),
        cancelled_by: userId || null,
      });
      if (res.error) {
        setError(res.error);
      } else {
        setShowCancelConfirm(false);
        loadData();
      }
    } catch (err: any) {
      setError(err.message || 'İptal sırasında hata.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReverse = async (movementId: string) => {
    if (!reverseReason.trim()) return;
    try {
      const { data: session } = await supabase.auth.getSession();
      const userId = session?.session?.user?.id;
      const res = await LoanDB.reverseMovement({
        movement_id: movementId,
        reversal_reason: reverseReason.trim(),
        reversed_by: userId || null,
      });
      if (res.error) {
        setError(res.error);
      } else {
        setReversingId(null);
        setReverseReason('');
        loadData();
      }
    } catch (err: any) {
      setError(err.message || 'Ters kayıt yapılamadı.');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin mb-4"></div>
        <h2 className="text-lg font-semibold text-foreground-700">Yükleniyor...</h2>
      </div>
    );
  }

  if (error || !loan) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-400"></i>
        </div>
        <h2 className="text-lg font-semibold text-foreground-700">{error || 'Kayıt bulunamadı'}</h2>
        <button onClick={() => navigate('/personal-loans')} className="btn-primary cursor-pointer">Listeye Dön</button>
      </div>
    );
  }

  const canRepay = loan.status !== 'Ödendi' && loan.status !== 'İptal';
  const canCancel = loan.status !== 'Ödendi' && loan.status !== 'İptal';

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <button onClick={() => navigate('/personal-loans')} className="text-sm text-foreground-400 hover:text-foreground-600 flex items-center gap-1 mb-1 cursor-pointer">
            <i className="ri-arrow-left-line"></i> Elden Borçlar
          </button>
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">{loan.loan_no}</h2>
          <p className="text-sm text-foreground-400 mt-0.5">{loan.borrower_name}</p>
        </div>
        <div className="flex items-center gap-2">
          {canRepay && (
            <button onClick={() => setShowRepayment(true)} className="btn-primary whitespace-nowrap flex items-center gap-2 cursor-pointer">
              <i className="ri-arrow-go-back-line"></i>
              <span>Geri Ödeme Al</span>
            </button>
          )}
          {canCancel && (
            <button onClick={() => setShowCancelConfirm(true)} className="px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-close-circle-line"></i>
              İptal
            </button>
          )}
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="stat-card p-4">
          <p className="text-xs text-foreground-400 mb-1">Verilen Tutar</p>
          <p className="text-xl font-bold text-foreground-900">{formatTRY(loan.principal_amount)}</p>
        </div>
        <div className="stat-card p-4">
          <p className="text-xs text-foreground-400 mb-1">Geri Alınan</p>
          <p className="text-xl font-bold text-emerald-600">{formatTRY(loan.paid_amount)}</p>
        </div>
        <div className="stat-card p-4">
          <p className="text-xs text-foreground-400 mb-1">Kalan</p>
          <p className="text-xl font-bold text-amber-600">{formatTRY(loan.remaining_amount)}</p>
        </div>
        <div className="stat-card p-4">
          <p className="text-xs text-foreground-400 mb-1">Durum</p>
          <span className={`text-sm px-3 py-1 rounded-full font-medium ${getStatusStyle(loan.status)}`}>{loan.status}</span>
        </div>
      </div>

      {/* Detail Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol: Detay */}
        <div className="lg:col-span-2 stat-card p-5 space-y-4">
          <h3 className="text-sm font-semibold text-foreground-800">Borç Detayı</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-foreground-400 text-xs mb-0.5">Kişi Türü</p>
              <p className="text-foreground-800 font-medium">{getBorrowerTypeLabel(loan.borrower_type)}</p>
            </div>
            <div>
              <p className="text-foreground-400 text-xs mb-0.5">Telefon</p>
              <p className="text-foreground-800 font-medium">{loan.borrower_phone || '—'}</p>
            </div>
            <div>
              <p className="text-foreground-400 text-xs mb-0.5">Borç Verme Tarihi</p>
              <p className="text-foreground-800 font-medium">{loan.loan_date}</p>
            </div>
            <div>
              <p className="text-foreground-400 text-xs mb-0.5">Son Ödeme Tarihi (Vade)</p>
              <p className={`font-medium ${loan.due_date && loan.due_date < new Date().toLocaleDateString('en-CA') && loan.remaining_amount > 0 ? 'text-red-600' : 'text-foreground-800'}`}>
                {loan.due_date || 'Belirtilmedi'}
              </p>
            </div>
            {loan.borrower_second_phone && (
              <div>
                <p className="text-foreground-400 text-xs mb-0.5">İkinci Telefon</p>
                <p className="text-foreground-800 font-medium">{loan.borrower_second_phone}</p>
              </div>
            )}
            {loan.borrower_address && (
              <div>
                <p className="text-foreground-400 text-xs mb-0.5">Adres</p>
                <p className="text-foreground-800 font-medium">{loan.borrower_address}</p>
              </div>
            )}
          </div>

          {(loan.guarantor_name || loan.guarantor_phone) && (
            <div className="pt-3 border-t border-background-200/70">
              <p className="text-xs text-foreground-400 mb-2">Kefil Bilgisi</p>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-foreground-800 font-medium">{loan.guarantor_name || '—'}</p>
                </div>
                <div>
                  <p className="text-foreground-800 font-medium">{loan.guarantor_phone || '—'}</p>
                </div>
              </div>
            </div>
          )}

          {(loan.description || loan.reason || loan.notes) && (
            <div className="pt-3 border-t border-background-200/70 space-y-2">
              {loan.reason && (
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Borç Verme Nedeni</p>
                  <p className="text-sm text-foreground-700">{loan.reason}</p>
                </div>
              )}
              {loan.description && (
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Açıklama</p>
                  <p className="text-sm text-foreground-700">{loan.description}</p>
                </div>
              )}
              {loan.notes && (
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Notlar</p>
                  <p className="text-sm text-foreground-700">{loan.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sağ: Hareket Geçmişi */}
        <div className="stat-card p-5">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4">Hareket Geçmişi</h3>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {movements.length === 0 ? (
              <p className="text-sm text-foreground-400 text-center py-4">Henüz hareket kaydı yok.</p>
            ) : (
              movements.map(mov => (
                <div key={mov.id} className={`flex items-start gap-3 pb-3 border-b border-background-100 last:border-0 ${mov.reversed_at ? 'opacity-50' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    mov.movement_type === 'loan_given' ? 'bg-amber-100' :
                    mov.movement_type === 'repayment' ? 'bg-emerald-100' :
                    mov.movement_type === 'reversal' || mov.movement_type === 'cancellation' ? 'bg-red-100' :
                    'bg-sky-100'
                  }`}>
                    <i className={`${getMovementIcon(mov.movement_type)} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-foreground-700">{getMovementLabel(mov.movement_type)}</p>
                      <p className={`text-sm font-bold whitespace-nowrap ml-2 ${
                        mov.movement_type === 'repayment' ? 'text-emerald-600' :
                        mov.movement_type === 'loan_given' ? 'text-amber-600' :
                        'text-foreground-700'
                      }`}>
                        {mov.movement_type === 'repayment' ? '+' : mov.movement_type === 'reversal' ? '±' : ''}{formatTRY(mov.amount)}
                      </p>
                    </div>
                    <p className="text-xs text-foreground-400">{mov.movement_date}{mov.description ? ` · ${mov.description}` : ''}</p>
                    {mov.receipt_no && <p className="text-xs text-foreground-400">Makbuz: {mov.receipt_no}</p>}
                    {mov.reversed_at && <p className="text-xs text-red-500">Ters kayıt: {mov.reversal_reason}</p>}
                    {!mov.reversed_at && mov.movement_type === 'repayment' && (
                      <button
                        onClick={() => { setReversingId(mov.id); setReverseReason(''); }}
                        className="text-xs text-red-400 hover:text-red-600 mt-1 cursor-pointer"
                      >
                        Ters Kayıt
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Repayment Modal */}
      {showRepayment && (
        <RepaymentModal
          loanId={loan.id}
          remainingAmount={loan.remaining_amount}
          borrowerName={loan.borrower_name}
          onClose={() => setShowRepayment(false)}
          onSuccess={() => { setShowRepayment(false); loadData(); }}
        />
      )}

      {/* Cancel Confirm Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCancelConfirm(false)}></div>
          <div className="relative bg-background-50 rounded-2xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground-900 mb-2">Borcu İptal Et</h3>
            <p className="text-sm text-foreground-500 mb-4">
              Bu işlem borç kaydını iptal edecek ve varsa kalan borç tutarı kasaya iade edilecektir. Bu işlem geri alınamaz.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">İptal Sebebi *</label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
                rows={3}
                placeholder="İptal sebebini açıklayın"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setShowCancelConfirm(false)} className="px-4 py-2 text-sm text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer whitespace-nowrap">
                Vazgeç
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling || !cancelReason.trim()}
                className="px-4 py-2 text-sm font-medium text-background-50 bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {cancelling ? 'İptal ediliyor...' : 'Borcu İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reverse Confirm Modal */}
      {reversingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setReversingId(null)}></div>
          <div className="relative bg-background-50 rounded-2xl shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-foreground-900 mb-2">Ödemeyi Ters Çevir</h3>
            <p className="text-sm text-foreground-500 mb-4">
              Bu işlem geri ödemeyi ters çevirir. Borç kalanı eski haline döner ve kasada ters çıkış yapılır.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ters Kayıt Sebebi *</label>
              <textarea
                value={reverseReason}
                onChange={e => setReverseReason(e.target.value)}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg focus:outline-none focus:border-primary-300 text-foreground-700 resize-none"
                rows={3}
                placeholder="Neden ters çevriliyor?"
              />
            </div>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => { setReversingId(null); setReverseReason(''); }} className="px-4 py-2 text-sm text-foreground-600 bg-background-100 rounded-lg hover:bg-background-200 cursor-pointer whitespace-nowrap">
                Vazgeç
              </button>
              <button
                onClick={() => handleReverse(reversingId)}
                disabled={!reverseReason.trim()}
                className="px-4 py-2 text-sm font-medium text-background-50 bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                Ters Kayıt Yap
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}